/**
 * Drafts & sent (Slack; core DraftsViewModel, logic/drafts): my unsent drafts across conversations (synced across my
 * devices) and the messages I sent lately. Rows look like Slack's: the conversation's picture, its name (or "Thread in
 * #x") with the time on the right, and the text in two lines with its mentions and emoji. The whole row opens the
 * conversation; the hover toolbar (or a right-click / long-press) has Edit and Delete for a draft (Delete asks first,
 * like Slack), Open and Copy link for a sent message. Rows are focusable: Enter opens, Delete / Backspace deletes a
 * draft. Sent messages are grouped by day. The Scheduled tab reuses ScheduledList with the same row.
 */
import type { Block } from "@bundle/contracts";
import type { DraftRow, DraftsViewModel, SentRow } from "@bundle/core";
import { ComposerText, Emoji, Profile, RichText as RT, Timeline, type DayLabel } from "@bundle/core";
import type { IconName } from "@bundle/tokens";
import { useState, type KeyboardEvent, type ReactNode } from "react";
import { Avatar, AvatarStack, Button, Divider, Icon, InlineText, List, Pressable, Sheet, Spinner, Stack, Text, type InlineRun, type MenuItem } from "../primitives/index.ts";
import type { MoreActions } from "../primitives/HoverActions.tsx";
import { useFormat, useLocale, useT } from "../i18n/I18nProvider.tsx";
import { useViewModel, useWorkspace } from "../hooks/index.ts";

// ---------- Pieces shared with the Scheduled list ----------

/** Loading, failed (with Retry) and empty states: a short headline and a helpful line, like Slack's. */
export function ListState({ icon, title, body, action, testID }: { icon?: IconName; title?: string; body?: string; action?: { label: string; onPress: () => void }; testID: string }) {
  return (
    <Stack grow align="center" justify="center" gap="3" padding="8" testID={testID}>
      {icon === undefined ? <Spinner /> : (
        <Stack width="avatar.xl" height="avatar.xl" align="center" justify="center" radius="full" background="bg.hover">
          <Icon name={icon} size="icon.lg" color="text.secondary" />
        </Stack>
      )}
      <Stack gap="1" align="center">
        {title === undefined ? null : (
          <Text variant="headline" weight="bold" align="center">
            {title}
          </Text>
        )}
        {body === undefined ? null : (
          <Text color="text.secondary" align="center">
            {body}
          </Text>
        )}
      </Stack>
      {action === undefined ? null : <Button variant="secondary" label={action.label} onPress={action.onPress} testID={`${testID}-retry`} />}
    </Stack>
  );
}

/** A conversation's picture: # or a lock for channels, the person for a DM, two people for a group DM. */
export function ConversationIcon({ channelId }: { channelId: string }) {
  const { state } = useWorkspace();
  const cache = state.cache;
  const c = cache?.channels[channelId];
  if (cache === undefined || c === undefined || c.kind === "public" || c.kind === "private") {
    return (
      <Stack width="avatar.message" height="avatar.message" align="center" justify="center" radius="md" background="bg.hover">
        <Icon name={c?.kind === "private" ? "lock" : "hash"} size="icon.md" color="text.secondary" />
      </Stack>
    );
  }
  const others = (c.dmUserIds ?? []).filter((id) => id !== cache.meId);
  const people = (others.length === 0 ? [cache.meId] : others).map((id) => {
    const m = cache.members[id];
    const look = m === undefined ? {} : Profile.avatarLook(m);
    return { name: m === undefined ? "?" : m.displayName, src: look.src, tone: look.tone, emoji: look.emoji, square: m?.kind === "agent" };
  });
  if (people.length > 1) return <AvatarStack people={people} />;
  const p = people[0]!;
  return <Avatar name={p.name} src={p.src} tone={p.tone} emoji={p.emoji} square={p.square} size="avatar.message" />;
}

export type ConversationRowProps = {
  channelId: string;
  /** "#design", "Bo Chen", "Thread in #engineering". */
  title: string;
  /** Right of the title (footnote, tertiary). */
  time?: string;
  /** Full date and time for the time's tooltip. */
  timeTitle?: string;
  /** Under the title: the message preview, a status line, the inline editor. */
  children?: ReactNode;
  /** The whole row opens (absent: the row is not pressable, e.g. while editing in place). */
  onPress?: () => void;
  /** Keys on the focused row besides Enter (Delete / Backspace deletes a draft). */
  onKey?: (key: string) => boolean;
  hoverActions: MenuItem[];
  moreActions?: MoreActions;
  accessibilityLabel: string;
  testID: string;
};

/** One full-width row of Drafts & sent / Scheduled: picture, bold title, time on the right, two lines of text. */
export function ConversationRow(p: ConversationRowProps) {
  const body = (
    <Stack direction="horizontal" align="start" gap="3" paddingX="3" paddingY="2" grow>
      <ConversationIcon channelId={p.channelId} />
      <Stack grow gap="0.5">
        <Stack direction="horizontal" align="center" gap="2">
          <Stack grow>
            <Text weight="bold" lines={1}>
              {p.title}
            </Text>
          </Stack>
          {p.time === undefined ? null : (
            <span className="b-conversation-time" title={p.timeTitle} style={{ flex: "none" }}>
              <Text variant="footnote" color="text.tertiary" selectable={false}>
                {p.time}
              </Text>
            </span>
          )}
        </Stack>
        {p.children}
      </Stack>
    </Stack>
  );
  const content =
    p.onPress === undefined ? (
      body
    ) : (
      <Pressable
        onPress={p.onPress}
        direction="horizontal"
        radius="md"
        // Right-click / long-press: the row's actions (touch screens have no hover toolbar). With `moreActions` the
        // Stack's own context menu has them.
        contextMenu={p.moreActions === undefined ? p.hoverActions : undefined}
        accessibilityLabel={p.accessibilityLabel}
        testID={`${p.testID}-open`}
        onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
          if (e.target !== e.currentTarget || p.onKey === undefined) return;
          if (p.onKey(e.key)) e.preventDefault();
        }}
      >
        {body}
      </Pressable>
    );
  // The toolbar sits in the row in place of the time (primitives.css .b-conversation-row).
  return (
    <div className="b-conversation-row">
      <Stack paddingX="5" hoverActions={p.hoverActions} moreActions={p.moreActions} testID={p.testID}>
        {content}
      </Stack>
    </div>
  );
}

/** Breathing room between the tabs and the first row. */
const LIST_TOP = <div style={{ height: 8 }} />;

/** "10:42 AM" today, "Yesterday", else "Oct 20" (with the year when it is not this year), like Slack's Drafts list. */
function useShortTime(): (ts: number) => string {
  const t = useT();
  const fmt = useFormat();
  const locale = useLocale();
  const { vm } = useWorkspace();
  const clock = vm.deps.clock;
  const now = clock.now();
  const offset = clock.tzOffsetMinutes(now);
  const today = Timeline.localDay(now, offset);
  return (ts) => {
    const day = Timeline.localDay(ts, offset);
    if (day === today) return fmt.time(ts);
    if (day === today - 1) return t("time.yesterday");
    const c = Timeline.civilFromDays(day);
    if (c.year !== Timeline.civilFromDays(today).year) return fmt.date({ year: c.year, month: c.month, day: c.day });
    return new Intl.DateTimeFormat(locale, { month: "short", day: "numeric", timeZone: "UTC" }).format(Date.UTC(c.year, c.month - 1, c.day));
  };
}

// ---------- Rich previews ----------

const SHORTCODE = /:([a-z0-9_+-]+):/gi;

/** A draft's text as inline runs: mention tokens as chips (like the composer shows them), :shortcodes: as emoji. */
function draftRuns(text: string, ctx: ComposerText.ComposerChipContext): InlineRun[] {
  const runs: InlineRun[] = [];
  for (const seg of ComposerText.composerSegments(text.replace(/\s+/g, " ").trim(), ctx)) {
    if (seg.type === "chip") runs.push({ text: seg.label, chip: seg.chip });
    else runs.push({ text: seg.text.replace(SHORTCODE, (all, name: string) => {
      const e = Emoji.displayEmoji(name);
      return e.startsWith(":") ? all : e;
    }) });
  }
  return runs;
}

/** A message's blocks flattened into one run of inline text (paragraphs, list items and quotes joined by spaces). */
function blockRuns(blocks: RT.RenderBlock[], out: InlineRun[] = []): InlineRun[] {
  for (const b of blocks) {
    if (out.length > 0) out.push({ text: " " });
    if (b.type === "paragraph") for (const r of b.runs) out.push(r.chip === undefined ? { ...r, text: r.text.replace(/\s+/g, " ") } : { text: r.text, chip: r.chip });
    else if (b.type === "code") out.push({ text: b.text.replace(/\s+/g, " "), code: true });
    else if (b.type === "quote") blockRuns(b.blocks, out);
    else for (const item of b.items) blockRuns(item, out);
  }
  return out;
}

function sentRuns(blocks: Block[], ctx: RT.RichTextContext): InlineRun[] {
  return blockRuns(RT.renderBlocks(blocks, ctx));
}

// ---------- Drafts ----------

/** The Drafts tab: most recent first; a row opens its conversation with the draft in the composer. */
export function DraftsList({ vm, onOpen, testID }: { vm: DraftsViewModel; onOpen: (row: DraftRow) => void; testID: string }) {
  const t = useT();
  const fmt = useFormat();
  const s = useViewModel(vm);
  const { state } = useWorkspace();
  const shortTime = useShortTime();
  const [confirm, setConfirm] = useState<DraftRow | undefined>(undefined);
  if (s.status === "loading" && s.drafts.length === 0) return <ListState testID={`${testID}-loading`} />;
  if (s.status === "failed" && s.drafts.length === 0) return <ListState icon="offline" body={t("drafts.failed")} action={{ label: t("later.retry"), onPress: () => void vm.refresh() }} testID={`${testID}-failed`} />;
  const rows = vm.rows();
  if (rows.length === 0) return <ListState icon="edit" title={t("drafts.empty.title")} body={t("drafts.empty")} testID={`${testID}-empty`} />;
  const cache = state.cache;
  const ctx = cache === undefined ? undefined : { meId: cache.meId, members: cache.members, channels: cache.channels, userGroups: cache.userGroups ?? {} };
  return (
    <>
      <List
        items={rows}
        keyOf={(row) => row.id}
        header={LIST_TOP}
        testID={`${testID}-list`}
        renderItem={(row) => {
          const title = row.inThread ? t("drafts.inThread", { conversation: row.conversation }) : row.conversation;
          return (
            <ConversationRow
              channelId={row.draft.channelId}
              title={title}
              time={shortTime(row.draft.updatedAt)}
              timeTitle={fmt.dateTime(row.draft.updatedAt)}
              onPress={() => onOpen(row)}
              onKey={(key) => {
                if (key !== "Delete" && key !== "Backspace") return false;
                setConfirm(row);
                return true;
              }}
              hoverActions={[
                { id: "edit", label: t("drafts.edit"), icon: "edit", onSelect: () => onOpen(row) },
                { id: "delete", label: t("drafts.delete"), icon: "trash", destructive: true, onSelect: () => setConfirm(row) },
              ]}
              accessibilityLabel={`${title}: ${row.preview}`}
              testID={`${testID}-row-${row.id}`}
            >
              {ctx === undefined ? <Text lines={2}>{row.preview}</Text> : <InlineText runs={draftRuns(row.draft.text, ctx)} lines={2} selectable={false} />}
            </ConversationRow>
          );
        }}
      />
      <Sheet open={confirm !== undefined} onClose={() => setConfirm(undefined)} title={t("drafts.deleteConfirm.title")} detents={["medium"]} closeLabel={t("common.close")} testID={`${testID}-delete-confirm`}>
        {confirm === undefined ? null : (
          <Stack gap="4">
            <Text color="text.secondary">{t("drafts.deleteConfirm.body")}</Text>
            <Stack gap="1" padding="3" radius="md" border="border.subtle" background="bg.surface">
              <Text weight="semibold" lines={1}>
                {confirm.inThread ? t("drafts.inThread", { conversation: confirm.conversation }) : confirm.conversation}
              </Text>
              <Text color="text.secondary" lines={3}>
                {confirm.preview}
              </Text>
            </Stack>
            <Stack direction="horizontal" gap="2" justify="end">
              <Button label={t("common.cancel")} variant="secondary" onPress={() => setConfirm(undefined)} testID={`${testID}-delete-cancel`} />
              <Button
                label={t("common.delete")}
                variant="destructive"
                onPress={() => {
                  vm.delete(confirm.id);
                  setConfirm(undefined);
                }}
                testID={`${testID}-delete-confirm-button`}
              />
            </Stack>
          </Stack>
        )}
      </Sheet>
    </>
  );
}

// ---------- Sent ----------

type SentItem = { type: "day"; key: string; label: DayLabel } | { type: "row"; key: string; row: SentRow };

/** The Sent tab: my latest messages, newest first, under day headers; a row opens the message in its conversation. */
export function SentList({ vm, onOpen, onCopyLink, testID }: { vm: DraftsViewModel; onOpen: (row: SentRow) => void; onCopyLink?: (row: SentRow) => void; testID: string }) {
  const t = useT();
  const fmt = useFormat();
  const s = useViewModel(vm);
  const { state, vm: ws } = useWorkspace();
  if (s.sentStatus === undefined || (s.sentStatus === "loading" && s.sent === undefined)) return <ListState testID={`${testID}-loading`} />;
  if (s.sentStatus === "failed" && s.sent === undefined) return <ListState icon="offline" body={t("drafts.sentFailed")} action={{ label: t("later.retry"), onPress: () => void vm.loadSent() }} testID={`${testID}-failed`} />;
  const rows = vm.sentRows();
  if (rows.length === 0) return <ListState icon="send" title={t("drafts.sentEmpty.title")} body={t("drafts.sentEmpty")} testID={`${testID}-empty`} />;
  const cache = state.cache;
  const ctx = cache === undefined ? undefined : { meId: cache.meId, members: cache.members, channels: cache.channels, userGroups: cache.userGroups ?? {}, privateChannelLabel: t("richtext.privateChannel") };
  const now = ws.deps.clock.now();
  const offset = ws.deps.clock.tzOffsetMinutes(now);
  const today = Timeline.localDay(now, offset);
  const items: SentItem[] = [];
  let lastDay: number | undefined;
  for (const row of rows) {
    const day = Timeline.localDay(row.message.createdAt, offset);
    if (day !== lastDay) items.push({ type: "day", key: `day-${day}`, label: Timeline.dayLabel(day, today) });
    lastDay = day;
    items.push({ type: "row", key: row.id, row });
  }
  return (
    <List
      items={items}
      keyOf={(it) => it.key}
      header={LIST_TOP}
      testID={`${testID}-list`}
      renderItem={(it) => {
        if (it.type === "day") {
          return (
            <Stack paddingX="8" paddingY="2" gap="2" testID={`${testID}-${it.key}`}>
              <Text variant="footnote" weight="bold" color="text.secondary">
                {fmt.day(it.label)}
              </Text>
              <Divider />
            </Stack>
          );
        }
        const row = it.row;
        const title = row.inThread ? t("drafts.inThread", { conversation: row.conversation }) : row.conversation;
        const runs = ctx === undefined ? [] : sentRuns(row.message.blocks, ctx);
        const actions: MenuItem[] = [{ id: "open", label: t("drafts.open"), icon: "arrowRight", onSelect: () => onOpen(row) }];
        if (onCopyLink !== undefined) actions.push({ id: "copy-link", label: t("message.copyLink"), icon: "link", onSelect: () => onCopyLink(row) });
        return (
          <ConversationRow
            channelId={row.message.channelId}
            title={title}
            time={fmt.time(row.message.createdAt)}
            timeTitle={fmt.dateTime(row.message.createdAt)}
            onPress={() => onOpen(row)}
            hoverActions={actions}
            accessibilityLabel={`${title}: ${row.preview}`}
            testID={`${testID}-row-${row.id}`}
          >
            {runs.length === 0 ? (
              <Text color="text.secondary" lines={2}>
                {row.preview}
              </Text>
            ) : (
              <InlineText runs={runs} lines={2} selectable={false} />
            )}
          </ConversationRow>
        );
      }}
    />
  );
}
